import { useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { jwtDecode } from "jwt-decode";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

import { API_URL, USER_TYPE_SYSTEM } from "../../helpers/constants";
import { getToken } from "../../helpers/tokenStorage";
import Constellation from "./Constellation";

const PLATFORM_HOME = "https://clue2app.ai";

/**
 * Public showcase of apps built on Clue2App.
 *
 * Route: /showcase (PUBLIC ROUTES of App.js — no auth wrapper).
 * Data: GET /api/showcase/entries (whitelisted in coordinator
 * SecurityConfig, returns only SYSTEM-APPROVED entries).
 *
 * Composition:
 *   - PublicTopBar — transparent over the hero, wordmark links to
 *     clue2app.ai (the platform marketing surface), Sign in goes to
 *     the console login.
 *   - Constellation — framer-motion orbit-around-core hero.
 *   - ModerationQueue — visible only to SYSTEM users; inline approval
 *     pipeline so the visitor can curate without leaving the page.
 *   - Grid — the canonical catalog of approved apps; glassy
 *     parallax-tilt tiles.
 */
export default function Showcase() {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);
  const [queue, setQueue] = useState([]);
  const [queueError, setQueueError] = useState(null);

  const currentUser = useCurrentUser();
  const isSystem = currentUser?.userType === USER_TYPE_SYSTEM;

  const reloadEntries = () => {
    fetch(`${API_URL}/showcase/entries`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setEntries)
      .catch((err) => setError(err.message || "Failed to load showcase"));
  };

  useEffect(() => {
    reloadEntries();
  }, []);

  useEffect(() => {
    if (!isSystem) return;
    loadQueue(setQueue, setQueueError);
  }, [isSystem]);

  // Page-specific document head — the global title is "Clue2App",
  // we want the /showcase tab and any link-preview to say something
  // more descriptive. Restore on unmount so other routes don't
  // inherit showcase metadata.
  useEffect(() => {
    const prevTitle = document.title;
    const prev = upsertMetaSnapshot();
    document.title = "Showcase — Built on Clue2App";
    upsertMeta("description", "Apps shipped by our customers, partners, and team — the constellation of work running on the Clue2App platform.");
    upsertMeta("og:title", "Showcase — Built on Clue2App", true);
    upsertMeta("og:description", "Apps shipped by our customers, partners, and team — the constellation of work running on the Clue2App platform.", true);
    upsertMeta("og:url", "https://console.clue2.app/showcase", true);
    return () => {
      document.title = prevTitle;
      restoreMetaSnapshot(prev);
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        background: "linear-gradient(180deg, #0a0e27 0%, #0f172a 60%, #131a3a 100%)",
        color: "rgba(255,255,255,0.92)",
      }}
    >
      <PublicTopBar />
      <Constellation entries={entries || []} />

      <Container maxWidth="lg" sx={{ pt: 6, pb: 10 }}>
        {isSystem && (
          <ModerationQueue
            queue={queue}
            error={queueError}
            onChanged={() => {
              loadQueue(setQueue, setQueueError);
              reloadEntries();
            }}
          />
        )}

        <ShowcaseGrid entries={entries} error={error} />
      </Container>

      <PublicFooter />
    </Box>
  );
}

// ----------------- top bar -----------------

function PublicTopBar() {
  return (
    <AppBar
      position="absolute"
      elevation={0}
      sx={{
        background: "transparent",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        color: "rgba(255,255,255,0.95)",
        zIndex: 10,
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        <Box
          component="a"
          href={PLATFORM_HOME}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <Box
            component="img"
            src={process.env.PUBLIC_URL + "/clue2app-logo.png"}
            alt="Clue2App"
            sx={{ height: 32, filter: "drop-shadow(0 0 8px rgba(234,88,12,0.4))" }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
            Clue2App
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            component="a"
            href={PLATFORM_HOME}
            sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none" }}
          >
            Platform home
          </Button>
          <Button
            component="a"
            href="/login"
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #EA580C 0%, #F97316 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #C2410C 0%, #EA580C 100%)",
                boxShadow: "0 4px 20px rgba(234,88,12,0.4)",
              },
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "0 2px 12px rgba(234,88,12,0.3)",
            }}
          >
            Sign in
          </Button>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

// ----------------- grid + tiles -----------------

function ShowcaseGrid({ entries, error }) {
  if (error) {
    return (
      <Typography sx={{ textAlign: "center", color: "#fca5a5", py: 4 }}>
        Could not load the showcase right now: {error}
      </Typography>
    );
  }
  if (entries === null) {
    return (
      <Typography sx={{ textAlign: "center", color: "rgba(255,255,255,0.5)", py: 4 }}>
        Loading…
      </Typography>
    );
  }
  if (entries.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          px: 3,
          border: "1px dashed rgba(255,255,255,0.1)",
          borderRadius: 3,
          color: "rgba(255,255,255,0.6)",
          background: "rgba(255,255,255,0.02)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Typography variant="h6" sx={{ mb: 1, color: "rgba(255,255,255,0.8)" }}>
          The first apps are landing soon
        </Typography>
        <Typography variant="body2">
          When project admins submit and our team approves them, they appear here.
        </Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 3,
          letterSpacing: "-0.01em",
          color: "rgba(255,255,255,0.95)",
        }}
      >
        The collection
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
          gap: 3,
        }}
      >
        {entries.map((e, idx) => (
          <ShowcaseTile key={`${e.url}-${e.name}`} entry={e} index={idx} />
        ))}
      </Box>
    </Box>
  );
}

function ShowcaseTile({ entry, index }) {
  // Mouse-following 3D parallax tilt — adds a tactile, almost
  // physical quality to the hover. The springs damp the motion so
  // the card glides rather than snaps.
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotX = useSpring(useTransform(y, [-0.5, 0.5], [10, -10]), { stiffness: 250, damping: 25 });
  const rotY = useSpring(useTransform(x, [-0.5, 0.5], [-10, 10]), { stiffness: 250, damping: 25 });

  const handleMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.a
      ref={ref}
      href={entry.url}
      target="_blank"
      rel="noreferrer"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: "easeOut" }}
      style={{
        display: "block",
        textDecoration: "none",
        rotateX: rotX,
        rotateY: rotY,
        transformStyle: "preserve-3d",
      }}
    >
      <Box
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
          border: "1px solid rgba(255,255,255,0.08)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          transition: "border-color 200ms, box-shadow 200ms",
          "&:hover": {
            borderColor: "rgba(253,186,116,0.4)",
            boxShadow:
              "0 16px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(234,88,12,0.3), 0 0 32px rgba(234,88,12,0.15)",
          },
        }}
      >
        {entry.imageUrl ? (
          <Box
            sx={{
              height: 180,
              position: "relative",
              overflow: "hidden",
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, transparent 60%, rgba(10,14,39,0.5) 100%)",
              },
            }}
          >
            <Box
              component="img"
              src={entry.imageUrl}
              alt={entry.name}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              height: 180,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%)",
              color: "#fff",
              fontWeight: 700,
              fontSize: "1.5rem",
              letterSpacing: "0.05em",
              textShadow: "0 2px 12px rgba(0,0,0,0.3)",
            }}
          >
            {entry.name}
          </Box>
        )}
        <Box sx={{ p: 2.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 0.5,
              color: "rgba(255,255,255,0.95)",
              letterSpacing: "-0.01em",
            }}
          >
            {entry.name}
          </Typography>
          {entry.category && (
            <Chip
              label={entry.category}
              size="small"
              sx={{
                mb: 1.5,
                background: "rgba(234,88,12,0.15)",
                color: "#fdba74",
                border: "1px solid rgba(234,88,12,0.3)",
                fontWeight: 500,
              }}
            />
          )}
          {entry.description && (
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)", lineHeight: 1.5 }}>
              {entry.description}
            </Typography>
          )}
        </Box>
      </Box>
    </motion.a>
  );
}

// ----------------- moderation queue (SYSTEM only) -----------------

function ModerationQueue({ queue, error, onChanged }) {
  return (
    <Box
      sx={{
        mb: 6,
        p: 3,
        border: "1px solid rgba(251,146,60,0.3)",
        background: "linear-gradient(135deg, rgba(234,88,12,0.08) 0%, rgba(15,23,42,0.4) 100%)",
        borderRadius: 3,
        backdropFilter: "blur(10px)",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: "#fdba74" }}>
        Moderation queue ({queue.length})
      </Typography>
      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mb: 2 }}>
        Project admins submitted these apps for the public showcase. Approve to publish; deny with a reason to reject.
      </Typography>
      {error && <Typography sx={{ color: "#fca5a5", mb: 2 }}>{error}</Typography>}
      {queue.length === 0 && !error && (
        <Typography sx={{ color: "rgba(255,255,255,0.5)" }}>Queue is empty.</Typography>
      )}
      <Stack spacing={2} divider={<Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />}>
        {queue.map((row) => (
          <QueueRow key={row.imageUid} row={row} onChanged={onChanged} />
        ))}
      </Stack>
    </Box>
  );
}

function QueueRow({ row, onChanged }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const act = (path, body) => {
    setBusy(true);
    moderate(row.imageUid, path, body)
      .then(() => onChanged())
      .catch((err) => alert(err.message || "Moderation failed"))
      .finally(() => setBusy(false));
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        {row.imageUrl ? (
          <Box
            component="img"
            src={row.imageUrl}
            alt=""
            sx={{ width: 96, height: 64, objectFit: "cover", borderRadius: 1 }}
          />
        ) : null}
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, color: "rgba(255,255,255,0.95)" }}>
            {row.appName}{" "}
            {row.category && <Chip label={row.category} size="small" sx={{ ml: 1, background: "rgba(234,88,12,0.2)", color: "#fdba74" }} />}
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
            <a href={row.showcaseUrl} target="_blank" rel="noreferrer" style={{ color: "#fdba74" }}>
              {row.showcaseUrl}
            </a>
          </Typography>
          {row.description && (
            <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(255,255,255,0.75)" }}>
              {row.description}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>
            Submitted by {row.submittedByEmail || "unknown"} on{" "}
            {row.submittedOn ? new Date(row.submittedOn).toLocaleString() : "—"}
          </Typography>
        </Box>
        <Stack spacing={1} sx={{ minWidth: 220 }}>
          <Button variant="contained" color="success" disabled={busy} onClick={() => act("approve")}>
            Approve
          </Button>
          <TextField
            size="small"
            placeholder="Deny reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "rgba(255,255,255,0.9)",
                "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
              },
              "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.4)" },
            }}
          />
          <Button variant="outlined" color="error" disabled={busy} onClick={() => act("deny", { reason })}>
            Deny
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

// ----------------- footer -----------------

function PublicFooter() {
  return (
    <Box
      sx={{
        borderTop: "1px solid rgba(255,255,255,0.06)",
        py: 4,
        textAlign: "center",
        color: "rgba(255,255,255,0.5)",
        fontSize: "0.875rem",
      }}
    >
      Want your app featured here? Open it in the console, head to the{" "}
      <Box component="span" sx={{ color: "#fdba74", fontWeight: 500 }}>
        Showcase
      </Box>{" "}
      tab, and submit. ·{" "}
      <Box component="a" href={PLATFORM_HOME} sx={{ color: "#fdba74", textDecoration: "none" }}>
        clue2app.ai
      </Box>
    </Box>
  );
}

// ----------------- hooks + helpers -----------------

function useCurrentUser() {
  return useMemo(() => {
    const token = getToken();
    if (!token) return null;
    try {
      return jwtDecode(token);
    } catch {
      return null;
    }
  }, []);
}

function loadQueue(setQueue, setError) {
  const token = getToken();
  if (!token) {
    setError("Sign in as a SYSTEM admin to moderate the queue.");
    return;
  }
  fetch(`${API_URL}/showcase/queue`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((data) => {
      setQueue(data);
      setError(null);
    })
    .catch((err) => setError(err.message || "Failed to load moderation queue"));
}

// Document <head> helpers — written inline to avoid the
// react-helmet dependency for one page. upsertMeta tags itself with
// data-showcase="1" so we can take a clean snapshot of the prior
// values on mount and put them back on unmount.

function upsertMeta(name, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertMetaSnapshot() {
  const names = ["description"];
  const props = ["og:title", "og:description", "og:url"];
  const snapshot = {};
  names.forEach((n) => {
    const el = document.head.querySelector(`meta[name="${n}"]`);
    snapshot[`name:${n}`] = el ? el.getAttribute("content") : null;
  });
  props.forEach((p) => {
    const el = document.head.querySelector(`meta[property="${p}"]`);
    snapshot[`property:${p}`] = el ? el.getAttribute("content") : null;
  });
  return snapshot;
}

function restoreMetaSnapshot(snapshot) {
  Object.entries(snapshot).forEach(([key, value]) => {
    const [kind, name] = key.split(":");
    const attr = kind === "property" ? "property" : "name";
    const el = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (value == null) {
      el && el.remove();
    } else if (el) {
      el.setAttribute("content", value);
    }
  });
}

function moderate(imageUid, path, body) {
  const token = getToken();
  return fetch(`${API_URL}/showcase/app/${imageUid}/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  }).then((r) =>
    r.ok ? r.json() : r.text().then((t) => Promise.reject(new Error(t || `HTTP ${r.status}`)))
  );
}
